import { ReactNode, useState } from 'react';
import { Collapse, makeStyles } from '@material-ui/core';
import { ActivityFilterOptions, ActivityFilters } from '../../api/types';
import { AdvancedFilters, FilterToolbar } from './ActivityFiltersPanelControls';
import { PrimaryFilters } from './ActivityPrimaryFilters';
import { useDateDraft, useIssueDraft } from './activityFilterState';

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  adminActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

export interface ActivityFiltersPanelProps {
  filters: ActivityFilters;
  options: ActivityFilterOptions;
  loadingOptions: boolean;
  getProjectLabel: (projectId: string) => string;
  getCreatorLabel: (creatorRef: string) => string;
  onFiltersChange: (patch: Partial<ActivityFilters>) => void;
  onReset: () => void;
  resetBusy?: boolean;
  administrativeActions?: ReactNode;
}

function countAdvancedFilters(
  props: ActivityFiltersPanelProps,
  issueInput: string,
  fromInput: string,
  toInput: string,
) {
  return [
    issueInput.trim(),
    props.filters.createdBy,
    fromInput,
    toInput,
  ].filter(Boolean).length;
}

function hasClearableFilters(
  props: ActivityFiltersPanelProps,
  advancedCount: number,
) {
  return Boolean(
    props.filters.status ||
      props.filters.azureProject ||
      props.filters.azureRepository ||
      advancedCount,
  );
}

export const ActivityFiltersPanel = (props: ActivityFiltersPanelProps) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [issueInput, setIssueInput] = useIssueDraft(
    props.filters.issueExternalId,
    props.onFiltersChange,
  );
  const dateDraft = useDateDraft(props.filters, props.onFiltersChange);
  const advancedCount = countAdvancedFilters(
    props,
    issueInput,
    dateDraft.fromInput,
    dateDraft.toInput,
  );
  return (
    <section className={classes.root} aria-label="Filtros de atividades">
      <PrimaryFilters {...props} />
      <FilterToolbar
        filters={props.filters}
        expanded={expanded}
        advancedCount={advancedCount}
        clearDisabled={
          !hasClearableFilters(props, advancedCount) || Boolean(props.resetBusy)
        }
        onExpandedChange={setExpanded}
        onFiltersChange={props.onFiltersChange}
        onReset={props.onReset}
      />
      <Collapse in={expanded} unmountOnExit>
        <div id="advanced-activity-filters">
          <AdvancedFilters
            {...props}
            issueInput={issueInput}
            onIssueInputChange={setIssueInput}
            dateDraft={dateDraft}
          />
        </div>
      </Collapse>
      {props.administrativeActions && (
        <div className={classes.adminActions}>
          {props.administrativeActions}
        </div>
      )}
    </section>
  );
};

export { buildActivityDateRange } from './activityFilterState';
